import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { REFERRALS_SLICE_NAME } from "./referrals.constants"
import {
  addOutsiderPayoutMethod,
  exportAdminTable,
  getAdminStatisticBlockChart,
  getAdminStatisticBlocks,
  getAdminTable,
  getAdminTableAlertsChart,
  getAdminTableRewardChart,
  getAffiliateAccountInfo,
  getAlertsByBusinessAccountId,
  getAlertsBySourceTable,
  getCountryCodes,
  getFaq,
  getInviteLink,
  getOutsiderBlocks,
  getOutsiderPayoutMethod,
  getOutsiderWithdrawalsHistory,
  getReferralBAOptions,
  getReferralBusinessAccountInfo,
  getReferralsOverviewData,
  getReferralsOverviewMainInfo,
  getReferralsTable,
  getTotalStatistics,
  getYourCommissions,
  inviteByEmail,
  outsiderWithdraw,
  updateOutsiderPayoutMethod
} from "./referrals.thunks"

/* ------------- Admin table columns ------------- */

export const adminTableDefaultColumns = [
  { field_id: "affiliate_full_name", label: "Affiliate", active: true },
  { field_id: "affiliate_email", label: "Affiliate email", active: true },
  { field_id: "categories", label: "User type", active: true },
  { field_id: "referral_full_name", label: "Invited referral", active: true },
  { field_id: "referral_email", label: "Invited referral email", active: true },
  { field_id: "referral_status", label: "Referral status", active: true },
  { field_id: "business_account_created", label: "Date", active: true },
  { field_id: "business_account_name", label: "Referral BA", active: true },
  { field_id: "business_account_status", label: "BA status", active: true },
  { field_id: "reward", label: "Reward", active: true },
  { field_id: "alerts", label: "Alerts", active: true }
]

export const pinnedCols = ["affiliate_full_name"]
export const editableColumns = adminTableDefaultColumns.filter(
  (c) => !pinnedCols.includes(c.field_id)
)

const adminTableFilterOptions = {
  business_account_status: [
    "pending",
    "active",
    "needs_help",
    "incomplete",
    "unenrolled",
    "failed",
    "inactive"
  ],
  referral_status: ["Active", "Invited", "Setup Pending", "Verified", "Unverified", "Unenrolled"]
}

/* ------------- Initial State ------------- */

const initialState = {
  inviteLink: '"https://app.chargeback.io/go-premium?promoCodeXXXXXXXXXXX"',
  earningsFilters: {
    period: "27 days",
    business_accounts: "all"
  },
  loading: false,
  baAlertsFetching: false,
  message: null,
  referralsTable: [],
  referralsTableFetching: false,
  baAlerts: null,
  commissions: [],
  faq: [],
  totalStatistics: null,
  totalStatisticsFetching: false,
  overviewData: null,
  overviewDataFetching: false,
  overviewMainInfo: null,
  overviewMainInfoFetching: false,
  referralBAOptions: [],
  alertsBySourceTable: null,
  alertsBySourceFetching: false,
  // The legacy reducer only ever created these four through `state.merge(...)`, so they
  // were missing from its INITIAL_STATE - Immer needs them declared up front.
  adminStatisticBlocks: null,
  adminStatisticBlocksFetching: false,
  adminStatisticBlockChart: null,
  adminStatisticBlockChartFetching: false,
  adminTable: null,
  adminTableFetching: false,
  adminTableExportData: null,
  adminTableExportFetching: false,
  adminTableFilters: {
    affiliate_full_name: [],
    affiliate_email: [],
    referral_full_name: [],
    referral_email: [],
    referral_status: "all",
    business_account_created: null,
    business_account_name: [],
    business_account_status: "all"
  },
  adminTableVisibleFilters: {
    affiliate_full_name: true,
    affiliate_email: true
  },
  adminTableFilterOptions: adminTableFilterOptions,
  adminTableColumns: [...editableColumns],
  adminTableAlertsChart: null,
  adminTableAlertsChartFetching: false,
  adminTableRewardChart: null,
  adminTableRewardChartFetching: false,

  outsiderBlocks: null,
  outsiderBlocksFetching: false,
  outsiderWithdraw: null,
  outsiderWithdrawFetching: false,
  outsiderPayoutMethod: null,
  outsiderPayoutMethodFetching: false,
  addOutsiderPayoutMethodFetching: false,
  updateOutsiderPayoutMethodFetching: false,
  outsiderWithdrawalsHistory: [],
  outsiderWithdrawalsHistoryFetching: false,
  referralBusinessAccount: null,
  referralBusinessAccountFetching: false,
  countryCodes: null,
  countryCodesFetching: false,
  affiliateAccountInfo: null,
  affiliateAccountInfoFetching: false
}

const slice = createSlice({
  name: REFERRALS_SLICE_NAME,
  initialState,
  reducers: {
    setEarningsFilters(state, { payload }) {
      state.earningsFilters = { ...state.earningsFilters, ...payload }
    },
    setAdminTableFilters(state, { payload }) {
      state.adminTableFilters = { ...state.adminTableFilters, ...payload?.filters }
      state.adminTableVisibleFilters = {
        ...state.adminTableVisibleFilters,
        ...payload?.visibleFilters
      }
    },
    setAdminTableColumns(state, { payload }) {
      state.adminTableColumns = payload?.columns
    },
    clearAdminTableFilters(state) {
      state.adminTableFilters = initialState.adminTableFilters
    },
    resetAdminTableExportData(state) {
      state.adminTableExportData = null
    },
    resetMessage(state) {
      state.message = null
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- invite by email ---------------- */
      .addCase(inviteByEmail.pending, (state) => {
        state.loading = true
      })
      .addCase(inviteByEmail.fulfilled, (state, { payload }) => {
        state.loading = false
        state.message = payload.message
      })
      .addCase(inviteByEmail.rejected, (state) => {
        state.loading = false
      })

      /* ---------------- invite link ---------------- */
      .addCase(getInviteLink.pending, (state) => {
        state.loading = true
      })
      .addCase(getInviteLink.fulfilled, (state, { payload }) => {
        state.loading = false
        state.inviteLink = payload.link
      })
      .addCase(getInviteLink.rejected, (state) => {
        state.loading = false
      })

      /* ---------------- your commissions ---------------- */
      .addCase(getYourCommissions.pending, (state) => {
        state.loading = true
      })
      .addCase(getYourCommissions.fulfilled, (state, { payload }) => {
        state.loading = false
        state.commissions = payload
      })
      .addCase(getYourCommissions.rejected, (state) => {
        state.loading = false
      })

      /* ---------------- faq ---------------- */
      .addCase(getFaq.pending, (state) => {
        state.loading = true
      })
      .addCase(getFaq.fulfilled, (state, { payload }) => {
        state.loading = false
        state.faq = payload
      })
      .addCase(getFaq.rejected, (state) => {
        state.loading = false
      })

      /* ---------------- referrals table ---------------- */
      .addCase(getReferralsTable.pending, (state) => {
        state.referralsTableFetching = true
      })
      .addCase(getReferralsTable.fulfilled, (state, { payload }) => {
        state.referralsTableFetching = false
        state.referralsTable = payload
      })
      .addCase(getReferralsTable.rejected, (state) => {
        state.referralsTableFetching = false
      })

      /* ---------------- alerts by business account ---------------- */
      .addCase(getAlertsByBusinessAccountId.pending, (state) => {
        state.baAlertsFetching = true
      })
      .addCase(getAlertsByBusinessAccountId.fulfilled, (state, { payload }) => {
        state.baAlertsFetching = false
        state.baAlerts = payload
      })
      .addCase(getAlertsByBusinessAccountId.rejected, (state) => {
        state.baAlertsFetching = false
      })

      /* ---------------- total statistics ---------------- */
      .addCase(getTotalStatistics.pending, (state) => {
        state.totalStatisticsFetching = true
      })
      .addCase(getTotalStatistics.fulfilled, (state, { payload }) => {
        state.totalStatisticsFetching = false
        state.totalStatistics = payload
      })
      .addCase(getTotalStatistics.rejected, (state) => {
        state.totalStatisticsFetching = false
      })

      /* ---------------- overview data ---------------- */
      .addCase(getReferralsOverviewData.pending, (state) => {
        state.overviewDataFetching = true
      })
      .addCase(getReferralsOverviewData.fulfilled, (state, { payload }) => {
        state.overviewDataFetching = false
        state.overviewData = payload
      })
      .addCase(getReferralsOverviewData.rejected, (state) => {
        state.overviewDataFetching = false
      })

      /* ---------------- overview main info ---------------- */
      .addCase(getReferralsOverviewMainInfo.pending, (state) => {
        state.overviewMainInfoFetching = true
      })
      .addCase(getReferralsOverviewMainInfo.fulfilled, (state, { payload }) => {
        state.overviewMainInfoFetching = false
        state.overviewMainInfo = payload
      })
      .addCase(getReferralsOverviewMainInfo.rejected, (state) => {
        state.overviewMainInfoFetching = false
      })

      /* ---------------- referral BA options ---------------- */
      .addCase(getReferralBAOptions.fulfilled, (state, { payload }) => {
        state.referralBAOptions = payload
      })

      /* ---------------- alerts by source ---------------- */
      .addCase(getAlertsBySourceTable.pending, (state) => {
        state.alertsBySourceFetching = true
      })
      .addCase(getAlertsBySourceTable.fulfilled, (state, { payload }) => {
        state.alertsBySourceFetching = false
        state.alertsBySourceTable = payload
      })
      .addCase(getAlertsBySourceTable.rejected, (state) => {
        state.alertsBySourceFetching = false
      })

      /* ---------------- admin statistic blocks ---------------- */
      .addCase(getAdminStatisticBlocks.pending, (state) => {
        state.adminStatisticBlocksFetching = true
      })
      .addCase(getAdminStatisticBlocks.fulfilled, (state, { payload }) => {
        state.adminStatisticBlocksFetching = false
        state.adminStatisticBlocks = payload
      })
      .addCase(getAdminStatisticBlocks.rejected, (state) => {
        state.adminStatisticBlocksFetching = false
      })

      /* ---------------- admin statistic block chart ---------------- */
      .addCase(getAdminStatisticBlockChart.pending, (state) => {
        state.adminStatisticBlockChartFetching = true
      })
      .addCase(getAdminStatisticBlockChart.fulfilled, (state, { payload }) => {
        state.adminStatisticBlockChartFetching = false
        state.adminStatisticBlockChart = payload
      })
      .addCase(getAdminStatisticBlockChart.rejected, (state) => {
        state.adminStatisticBlockChartFetching = false
      })

      /* ---------------- admin table ---------------- */
      .addCase(getAdminTable.pending, (state) => {
        state.adminTableFetching = true
      })
      .addCase(getAdminTable.fulfilled, (state, { payload }) => {
        state.adminTableFetching = false
        state.adminTable = payload
      })
      .addCase(getAdminTable.rejected, (state) => {
        state.adminTableFetching = false
      })

      /* ---------------- admin table export ---------------- */
      .addCase(exportAdminTable.pending, (state) => {
        state.adminTableExportFetching = true
      })
      .addCase(exportAdminTable.fulfilled, (state, { payload }) => {
        state.adminTableExportFetching = false
        state.adminTableExportData = payload
      })
      .addCase(exportAdminTable.rejected, (state) => {
        state.adminTableExportFetching = false
      })

      /* ---------------- admin table alerts chart ---------------- */
      .addCase(getAdminTableAlertsChart.pending, (state) => {
        state.adminTableAlertsChartFetching = true
      })
      .addCase(getAdminTableAlertsChart.fulfilled, (state, { payload }) => {
        state.adminTableAlertsChartFetching = false
        state.adminTableAlertsChart = payload
      })
      .addCase(getAdminTableAlertsChart.rejected, (state) => {
        state.adminTableAlertsChartFetching = false
      })

      /* ---------------- admin table reward chart ---------------- */
      .addCase(getAdminTableRewardChart.pending, (state) => {
        state.adminTableRewardChartFetching = true
      })
      .addCase(getAdminTableRewardChart.fulfilled, (state, { payload }) => {
        state.adminTableRewardChartFetching = false
        state.adminTableRewardChart = payload
      })
      .addCase(getAdminTableRewardChart.rejected, (state) => {
        state.adminTableRewardChartFetching = false
      })

      /* ---------------- referral business account info ---------------- */
      .addCase(getReferralBusinessAccountInfo.pending, (state) => {
        state.referralBusinessAccountFetching = true
      })
      .addCase(getReferralBusinessAccountInfo.fulfilled, (state, { payload }) => {
        state.referralBusinessAccountFetching = false
        state.referralBusinessAccount = payload
      })
      .addCase(getReferralBusinessAccountInfo.rejected, (state) => {
        state.referralBusinessAccountFetching = false
      })

      /* ---------------- affiliate account info ---------------- */
      .addCase(getAffiliateAccountInfo.pending, (state) => {
        state.affiliateAccountInfoFetching = true
      })
      .addCase(getAffiliateAccountInfo.fulfilled, (state, { payload }) => {
        state.affiliateAccountInfoFetching = false
        state.affiliateAccountInfo = payload
      })
      .addCase(getAffiliateAccountInfo.rejected, (state) => {
        state.affiliateAccountInfoFetching = false
      })

      /* ---------------- country codes ---------------- */
      .addCase(getCountryCodes.pending, (state) => {
        state.countryCodesFetching = true
      })
      .addCase(getCountryCodes.fulfilled, (state, { payload }) => {
        state.countryCodesFetching = false
        state.countryCodes = payload
      })
      .addCase(getCountryCodes.rejected, (state) => {
        state.countryCodesFetching = false
      })

      /* ---------------- outsider blocks ---------------- */
      .addCase(getOutsiderBlocks.pending, (state) => {
        state.outsiderBlocksFetching = true
      })
      .addCase(getOutsiderBlocks.fulfilled, (state, { payload }) => {
        state.outsiderBlocksFetching = false
        state.outsiderBlocks = payload
      })
      .addCase(getOutsiderBlocks.rejected, (state) => {
        state.outsiderBlocksFetching = false
      })

      /* ---------------- outsider withdraw ---------------- */
      .addCase(outsiderWithdraw.pending, (state) => {
        state.outsiderWithdrawFetching = true
      })
      .addCase(outsiderWithdraw.fulfilled, (state, { payload }) => {
        state.outsiderWithdrawFetching = false
        state.outsiderWithdraw = payload
        state.message = payload?.message ?? state.message
      })
      .addCase(outsiderWithdraw.rejected, (state) => {
        state.outsiderWithdrawFetching = false
      })

      /* ---------------- outsider payout method ---------------- */
      .addCase(getOutsiderPayoutMethod.pending, (state) => {
        state.outsiderPayoutMethodFetching = true
      })
      .addCase(getOutsiderPayoutMethod.fulfilled, (state, { payload }) => {
        state.outsiderPayoutMethodFetching = false
        state.outsiderPayoutMethod = payload
      })
      .addCase(getOutsiderPayoutMethod.rejected, (state) => {
        state.outsiderPayoutMethodFetching = false
      })

      .addCase(addOutsiderPayoutMethod.pending, (state) => {
        state.addOutsiderPayoutMethodFetching = true
      })
      .addCase(addOutsiderPayoutMethod.fulfilled, (state, { payload }) => {
        state.addOutsiderPayoutMethodFetching = false
        state.outsiderPayoutMethod = payload
        state.message = payload?.message ?? state.message
      })
      .addCase(addOutsiderPayoutMethod.rejected, (state) => {
        state.addOutsiderPayoutMethodFetching = false
      })

      .addCase(updateOutsiderPayoutMethod.pending, (state) => {
        state.updateOutsiderPayoutMethodFetching = true
      })
      .addCase(updateOutsiderPayoutMethod.fulfilled, (state, { payload }) => {
        state.updateOutsiderPayoutMethodFetching = false
        state.outsiderPayoutMethod = payload
        state.message = payload?.message ?? state.message
      })
      .addCase(updateOutsiderPayoutMethod.rejected, (state) => {
        state.updateOutsiderPayoutMethodFetching = false
      })

      /* ---------------- outsider withdrawals history ---------------- */
      .addCase(getOutsiderWithdrawalsHistory.pending, (state) => {
        state.outsiderWithdrawalsHistoryFetching = true
      })
      .addCase(getOutsiderWithdrawalsHistory.fulfilled, (state, { payload }) => {
        state.outsiderWithdrawalsHistoryFetching = false
        state.outsiderWithdrawalsHistory = payload
      })
      .addCase(getOutsiderWithdrawalsHistory.rejected, (state) => {
        state.outsiderWithdrawalsHistoryFetching = false
      })

      /* ---------------- saved per-user table preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["referrals-admin-table"]?.data
        if (!preference || preference.version !== REFERRALS_ADMIN_TABLE_PREFERENCES_VERSION) return
        if (
          !preference.adminTableFilters ||
          !preference.adminTableColumns ||
          !preference.adminTableVisibleFilters
        )
          return

        state.adminTableFilters = preference.adminTableFilters
        state.adminTableColumns = preference.adminTableColumns.filter(
          ({ field_id }) => !pinnedCols.includes(field_id)
        )
        state.adminTableVisibleFilters = preference.adminTableVisibleFilters
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)
  }
})

export const {
  setEarningsFilters,
  setAdminTableFilters,
  setAdminTableColumns,
  clearAdminTableFilters,
  resetAdminTableExportData,
  resetMessage
} = slice.actions

/* ------------- Selectors ------------- */

export const selectAreAdminTableFiltersDefault = (state) =>
  isEqual(state[REFERRALS_SLICE_NAME].adminTableFilters, initialState.adminTableFilters)

// Bump when the shape of the saved `{ version, adminTableFilters, adminTableColumns,
// adminTableVisibleFilters }` payload changes.
export const REFERRALS_ADMIN_TABLE_PREFERENCES_VERSION = 2

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: REFERRALS_SLICE_NAME,
    storage,
    // Only the user's own filter/column setup survives a reload - every table, chart and
    // statistic block is re-fetched on mount.
    whitelist: [
      "earningsFilters",
      "adminTableFilters",
      "adminTableVisibleFilters",
      "adminTableColumns"
    ],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
